import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { ParkingService } from '../../../../../services/parking.service';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';

@Component({
  selector: 'app-open-gate-form',
  standalone: false,
  templateUrl: './open-gate-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './open-gate-form.component.css'
})
export class OpenGateFormComponent {
  componentDestroyed$: Subject<boolean> = new Subject();
  isLoading: boolean = false;
  activeRequests: number = 0;
  openGateForm: UntypedFormGroup;
  orderDetails: any;

  setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  incrementRequests() {
    this.activeRequests++;
    if (this.activeRequests > 0) {
      this.setIsLoading(true);
    }
    console.log('activeRequests', this.activeRequests);
  }

  decrementRequests() {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.activeRequests === 0) {
      this.setIsLoading(false);
    }
    console.log('activeRequests', this.activeRequests);
  }
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      camera: any,
      orgId: string
    },
    public dialogRef: MatDialogRef<OpenGateFormComponent>,
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private parkingService: ParkingService,
    public backOfficeService: BackOfficeService,
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.openGateForm = this.fb.group({
      RegNr: ['', [Validators.required]],
      orderId: ['', [Validators.required]],
      code: [this.data.camera.externalId, [Validators.required]],
    });
  }

  async onSubmitCheckInOrOutAndOpenGate() {
    if (!this.orderDetails.orderParkingId || !this.orderDetails.carId) {
      this.snackBar.open('Order not found', 'OK', {
        verticalPosition: 'top',
        duration: 2000
      });
      return;
    }

    if (this.openGateForm.valid) {

      //check if vehicle is at correct parking
      // console.log(this.data.camera.parkings);
      // console.log(this.orderDetails.orderParkingId);
      const isVehicleInParking = this.data.camera.parkings.some(parking => parking.id === this.orderDetails.parkingId);
      if (!isVehicleInParking) {
        this.snackBar.open(`Vehicle should go to parking ${this.orderDetails.parking}`, 'OK', {
          verticalPosition: 'top',
          duration: 2000
        });
        return;
      }

      try {
        if (this.data.camera.gateType === 'Checkin') {
          await this.checkInParking(this.openGateForm.value.RegNr, this.orderDetails.orderParkingId);
          await this.openGate(this.data.camera.externalId);
        } else if (this.data.camera.gateType === 'Checkout') {
          await this.checkOutParking(this.openGateForm.value.RegNr, this.orderDetails.orderParkingId);
          await this.openGate(this.data.camera.externalId);
        }
      } catch (error) {
        console.error('Error in onSubmitCheckInAndOpenGate:', error);
        this.snackBar.open('Ett fel uppstod vid hanteringen', 'OK', {
          verticalPosition: 'top',
          duration: 2000
        });
      }
    }
  }

  onSubmitOpenGate() {
    this.openGate(this.data.camera.externalId);
  }

  onCancel() {
    this.dialogRef.close();
  }

  openGate(code: string): Promise<void> {
    this.incrementRequests();
    return new Promise((resolve, reject) => {
      this.backOfficeService.openParkingGate(this.data.orgId, code)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: () => {
            resolve();
          },
          error: (error) => {
            this.decrementRequests();
            reject(error);
          },
          complete: () => {
            this.decrementRequests();
          }
        });
    });
  }

  checkInParking(carId: string, orderParkingId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const checkIn = moment().format('YYYY-MM-DDTHH:mm');
      const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
        data: {
          message: `Är du säker på att du vill checka in ${carId}?`,
          buttonText: {
            ok: 'Ja',
            cancel: 'Avbryt'
          }
        }
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed) {
          reject(new Error('Check-in cancelled by user'));
          return;
        }

        this.incrementRequests();
        this.parkingService
          .setStatusCheckIn(orderParkingId, checkIn)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: (orderParking) => {
              this.snackBar.open('Bilen är incheckad', 'OK', {
                verticalPosition: 'top',
                duration: 2000
              });
              resolve();
            },
            error: (error) => {
              this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                verticalPosition: 'top'
              });
              this.decrementRequests();
              reject(error);
            },
            complete: () => {
              this.fetchOrderDetailsFromRegNr();
              this.decrementRequests();
            }
          });
      });
    });
  }

  checkOutParking(carId: string, orderParkingId: string) {
    return new Promise<void>((resolve, reject) => {
      const checkOut = moment().format('YYYY-MM-DD HH:mm');
      const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
        data: {
          message: `Är du säker på att du vill checka ut ${carId}?`,
          buttonText: {
            ok: 'Ja',
            cancel: 'Avbryt'
          }
        }
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.incrementRequests();
          this.parkingService
            .setStatusCheckOut(orderParkingId, checkOut)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
              next: (orderParking) => {
                this.snackBar.open('Bilen är utcheckad', 'OK', {
                  verticalPosition: 'top',
                  duration: 2000
                });
                resolve();
              },
              error: (error) => {
                this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
                  verticalPosition: 'top'
                });
                // this.decrementRequests();
                reject(error);
              },
              complete: () => {
                this.fetchOrderDetailsFromRegNr();
                this.decrementRequests();
              }
            });
        }
      });
    });
  }

  async searchOrderDetails() {
    try {
      if (this.openGateForm.controls['orderId'].value && this.openGateForm.controls['orderId'].value !== '') {
        await this.fetchOrderDetailsFromOrderNr();
        // await this.fetchOrderServiceDetailsFromOrderNr(this.orderDetails.orderId);
      } else if (this.openGateForm.controls['RegNr'].value && this.openGateForm.controls['RegNr'].value !== '') {
        await this.fetchOrderDetailsFromRegNr();
      }
    } catch (error) {
      console.error('Error in searchOrderDetails:', error);
      this.snackBar.open('Error fetching order details', 'OK', {
        verticalPosition: 'top',
        duration: 2000
      });
    }
  }

  fetchOrderDetailsFromRegNr() {
    this.incrementRequests();
    this.backOfficeService
      .getOrderParkingDetailsCarId(this.openGateForm.controls['RegNr'].value)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (orderParkingVM) => {
        // console.log('orderParkingVM', orderParkingVM);
        this.orderDetails = orderParkingVM;

        this.orderDetails.orderParkingId = orderParkingVM.orderParkingId;
        this.openGateForm.get('orderId').patchValue(orderParkingVM.orderNr, { emitEvent: false });
        this.orderDetails.carId = orderParkingVM.carId;
        this.openGateForm.get('RegNr').patchValue(this.orderDetails.carId, { emitEvent: false });
      },
      error: (error) => {
        console.error('Error fetching order details:', error);
        this.decrementRequests();
      },
      complete: () => {
        this.decrementRequests();
      }
    });
  }

  fetchOrderDetailsFromOrderNr(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.incrementRequests();
      this.backOfficeService
        .getOrderNr(this.openGateForm.controls['orderId'].value)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (order) => {
            this.orderDetails = order;
            this.openGateForm.get('RegNr').patchValue(this.orderDetails.carId, { emitEvent: false });
            this.fetchOrderDetailsFromRegNr();
            resolve(order);
          },
          error: (error) => {
            console.error('Error fetching order details:', error);
            reject(error);
            this.decrementRequests();
          },
          complete: () => {
            this.decrementRequests();
          }
        });
    });
  }

  fetchOrderServiceDetailsFromOrderNr(orderId: string) {
    this.incrementRequests();
    this.backOfficeService
      .getOrderRows(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (orderRows) => {
        if (orderRows) {
          console.log('orderRows : ', orderRows);
          if (orderRows.orderParkings.length > 0) {
            this.orderDetails = orderRows.orderParkings[0];

            this.orderDetails.carId = orderRows.orderParkings[0].carId;
            this.openGateForm.get('RegNr').patchValue(this.orderDetails.carId, { emitEvent: false });

            // this.orderDetails.orderParkingId = orderRows.orderParkings[0].orderParkingId;
            // this.orderDetails.parkingId = orderRows.orderParkings[0].parkingId;
            // this.orderDetails.parking = orderRows.orderParkings[0].parking;
          }
        }
      },
      error: (error) => {
        console.error('Error fetching order details:', error);
        this.decrementRequests();
      },
      complete: () => {
        this.decrementRequests();
      }
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
